import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Rect } from '@embedpdf/models'
import type { RedactionItem } from '@embedpdf/plugin-redaction'
import { useRedaction } from '@embedpdf/plugin-redaction/react'
import { getCurrentStyle, setStyleUpdates } from '../../stores/styleStore'
import { Icon } from '../ui/icon'

interface MenuWrapperProps {
  style: React.CSSProperties
  ref: (el: HTMLDivElement | null) => void
}

interface RedactionSelectionContext {
  type: 'redaction'
  item: RedactionItem
  pageIndex: number
}

interface RedactionSelectionMenuProps {
  documentId: string
  selected: boolean
  menuWrapperProps: MenuWrapperProps
  placement: { suggestTop: boolean }
  rect: Rect
  context: RedactionSelectionContext
}

const MENU_HEIGHT = 36
const MENU_GAP = 4

export const RedactionSelectionMenu = ({
  documentId,
  selected,
  menuWrapperProps,
  placement,
  rect,
  context,
}: RedactionSelectionMenuProps) => {
  const { t } = useTranslation()
  const { provides: redactionApi } = useRedaction(documentId)
  const [showColor, setShowColor] = useState(false)
  const [color, setColor] = useState(getCurrentStyle().fillColor)

  if (!selected) return null

  const handleCommit = () => {
    redactionApi?.commitPending(context.item.page, context.item.id)
  }

  const handleRemove = () => {
    redactionApi?.removePending(context.item.page, context.item.id)
  }

  const handleColorChange = (c: string) => {
    setColor(c)
    setStyleUpdates({ fillColor: c })
  }

  const { style: wrapperStyle, ref } = menuWrapperProps
  const menuHeight = showColor ? 64 : MENU_HEIGHT
  const menuTop = placement.suggestTop ? -(menuHeight + MENU_GAP) : rect.size.height + MENU_GAP

  return (
    <div ref={ref} style={wrapperStyle}>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: menuTop,
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center rounded-lg bg-bg-surface shadow-lg border border-border-default px-1 py-1">
            <button
              onClick={() => setShowColor(!showColor)}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-blue-500/20 text-blue-400 cursor-pointer"
              title={t('tooltips.styleSettings') || 'Style'}
            >
              <Icon icon="palette" size={16} />
            </button>
            <button
              onClick={handleCommit}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-green-500/20 text-green-500 cursor-pointer"
              title={t('redaction.apply')}
            >
              <Icon icon="check" size={16} />
            </button>
            <button
              onClick={handleRemove}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-red-500/20 text-red-500 cursor-pointer"
              title={t('redaction.remove')}
            >
              <Icon icon="trash" size={16} />
            </button>
          </div>
          {showColor && (
            <div
              className="flex items-center gap-2 rounded-lg bg-bg-surface shadow-lg border border-border-default px-2 py-1.5"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseDownCapture={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              <span className="text-xs text-fg-muted">{t('style.fillColor') || 'Fill'}</span>
              <input
                type="color"
                value={color}
                onChange={(e) => handleColorChange(e.target.value)}
                className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent p-0"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
